import React, { useEffect, useRef, useState } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import { useDebounce } from '../../../common/Debounce.hooks';
import { User } from '../../../user/User.interface';
import { useFindUsers } from '../../../user/User.hooks';

export interface SingleUserSelectorProps {
  value?: number;
  disabled?: boolean;
  textFieldProps?: Omit<TextFieldProps, 'variant'>;
  onSelected?: (user: User) => void;
  onChange?: (user: User | null) => void;
}

const SingleUserSelector: React.FC<SingleUserSelectorProps> = (
  props: SingleUserSelectorProps
) => {
  const {
    value: userId,
    disabled,
    textFieldProps,
    onChange,
    onSelected
  } = props;
  const previousUserId = useRef<number | undefined>(userId);
  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<User | undefined>();
  const [users, setUsers] = useState<User[]>([]);
  const debouncedSearch: string = useDebounce(search, 300);
  const { userList, isLoading, error, get } = useFindUsers();

  useEffect(() => {
    const params: ApiParams = {};
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    get(params);
  }, [get, debouncedSearch]);

  useEffect(() => {
    userList?.data && setUsers(userList?.data);
  }, [userList]);

  // if userId changes, reselect user
  useEffect(() => {
    if (previousUserId.current !== userId) {
      setSelected(undefined);
      previousUserId.current = userId;
    }
  }, [userId]);

  useEffect(() => {
    // if no selected user yet
    if (selected) {
      return;
    }
    const selectedUser: User | undefined = users.find(
      (user: User) => user.id === userId
    );
    if (selectedUser) {
      setSelected(selectedUser);
      onSelected?.(selectedUser);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, userId]);

  return (
    <Autocomplete
      loading={isLoading}
      disabled={disabled}
      value={selected || null}
      options={users}
      getOptionLabel={(option: User) => {
        return `${option.firstName} ${option.lastName}`;
      }}
      getOptionSelected={(option: User) => option.id === userId}
      onInputChange={(_, value: string) => setSearch(value)}
      onChange={(_event: React.ChangeEvent<{}>, value: User | null) => {
        if (value) {
          previousUserId.current = value.id;
        }
        setSelected(value || undefined);
        onChange?.(value);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select User'
          placeholder='Choose a User'
          helperText={error?.message}
          {...textFieldProps}
        />
      )}
    />
  );
};

export default SingleUserSelector;
